
import React, { useState, useEffect } from 'react';
import { Gift, Box, Coins, Plus, LayoutTemplate, Trash2, Database, Link, AlertCircle, Percent, Package, Image as ImageIcon, Edit2, Sparkles, Clock, CheckCircle2, Loader2, X } from 'lucide-react';
import { LootTable, LootItem, RewardLog, DigitalReward, MysterySponsorship } from '../types';
import { fetchLootTables, createLootTable, updateLootTable, deleteLootTable, fetchRewardLogs, createDigitalReward, fetchDigitalRewards, deleteDigitalReward } from '../services/rewardService';
import { createMysterySponsorship, fetchMySponsorships, cancelMysterySponsorship } from '../services/sponsorshipService';

const ResearchRewardView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'PHYSICAL' | 'DIGITAL'>('PHYSICAL');
  const [loading, setLoading] = useState(false);
  const [lootTables, setLootTables] = useState<LootTable[]>([]);
  const [digitalRewards, setDigitalRewards] = useState<DigitalReward[]>([]);
  const [logs, setLogs] = useState<RewardLog[]>([]);
  
  // Create/Edit Physical State
  const [isCreatingPhysical, setIsCreatingPhysical] = useState(false);
  const [editingLootTableId, setEditingLootTableId] = useState<string | null>(null);
  const [physicalName, setPhysicalName] = useState('');
  const [physicalDesc, setPhysicalDesc] = useState('');
  const [newItems, setNewItems] = useState<LootItem[]>([]);
  const [isMystery, setIsMystery] = useState(false);
  const [pointCost, setPointCost] = useState<number>(100);
  
  // Community Mystery Box sponsorship state
  const [sponsorships, setSponsorships] = useState<MysterySponsorship[]>([]);
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorAmount, setSponsorAmount] = useState<number>(5);
  const [sponsorCurrency, setSponsorCurrency] = useState('USD');
  const [sponsorBusy, setSponsorBusy] = useState(false);
  const [sponsorMsg, setSponsorMsg] = useState<string | null>(null);

  // Create Digital State
  const [isCreatingDigital, setIsCreatingDigital] = useState(false);
  const [digiName, setDigiName] = useState('');
  const [digiAmount, setDigiAmount] = useState<number>(0.5);
  const [digiCurrency, setDigiCurrency] = useState('USDT');
  const [digiChain, setDigiChain] = useState('BSC');
  const [digiContract, setDigiContract] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
        const [tables, digi, recentLogs, spons] = await Promise.all([fetchLootTables(), fetchDigitalRewards(), fetchRewardLogs(), fetchMySponsorships()]);
        setLootTables(tables);
        setDigitalRewards(digi);
        setLogs(recentLogs);
        setSponsorships(spons);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSponsor = async () => {
      if (!sponsorName.trim() || sponsorAmount <= 0) return;
      setSponsorBusy(true);
      setSponsorMsg(null);
      try {
          await createMysterySponsorship(null, sponsorName.trim(), sponsorAmount, sponsorCurrency);
          setSponsorName('');
          setSponsorAmount(5);
          setSponsorMsg('Sponsorship recorded. Pay via KHQR to drop the prize into the Community Box (payment coming soon — an admin can confirm in the meantime).');
          loadData();
      } catch (e: any) {
          setSponsorMsg(e?.message || 'Could not record sponsorship.');
      } finally { setSponsorBusy(false); }
  };

  const handleCancelSponsorship = async (id: string) => {
      try { await cancelMysterySponsorship(id); loadData(); } catch (e) { console.error(e); }
  };

  const resetPhysicalForm = () => {
      setIsCreatingPhysical(false);
      setEditingLootTableId(null);
      setPhysicalName('');
      setPhysicalDesc('');
      setNewItems([]);
      setIsMystery(false);
      setPointCost(100);
  };

  const handleEditPhysical = (table: LootTable) => {
      setEditingLootTableId(table.id);
      setPhysicalName(table.name);
      setPhysicalDesc(table.description || '');
      setNewItems(table.items || []);
      setIsMystery(!!table.is_mystery);
      setPointCost(table.point_cost ?? 100);
      setIsCreatingPhysical(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCreateOrUpdatePhysical = async () => {
      if (!physicalName) return;
      try {
          if (editingLootTableId) {
              await updateLootTable(editingLootTableId, physicalName, physicalDesc, newItems, isMystery, pointCost);
          } else {
              await createLootTable(physicalName, physicalDesc, newItems, isMystery, pointCost);
          }
          resetPhysicalForm();
          loadData();
      } catch (e) { 
          alert(`Failed to ${editingLootTableId ? 'update' : 'create'} loot table`); 
      }
  };

  const updateItem = (idx: number, field: keyof LootItem, val: any) => {
      const updated = [...newItems];
      updated[idx] = { ...updated[idx], [field]: val };
      setNewItems(updated);
  };

  const addItem = () => {
      setNewItems([...newItems, { id: Date.now().toString(), name: '', type: 'PHYSICAL', value: 10, probability: 10, total_qty: 100, remaining_qty: 100, image_url: '' }]);
  };

  const removeItem = (idx: number) => { setNewItems(newItems.filter((_, i) => i !== idx)); };

  const handleDeletePhysical = async (id: string) => {
      if(!window.confirm("Delete this physical reward table?")) return;
      try { await deleteLootTable(id); loadData(); } catch (e) { console.error(e); }
  };

  const handleCreateDigital = async () => {
      if (!digiName || digiAmount <= 0) return;
      try {
          await createDigitalReward(digiName, digiAmount, digiCurrency, digiChain, digiContract);
          setIsCreatingDigital(false);
          setDigiName('');
          setDigiAmount(0.5);
          setDigiContract('');
          loadData();
      } catch (e) { alert('Failed to create digital reward'); }
  };

  const handleDeleteDigital = async (id: string) => {
      if(!window.confirm("Delete this digital reward configuration?")) return;
      try { await deleteDigitalReward(id); loadData(); } catch (e) { console.error(e); }
  };

  const calculateTotalProbability = () => {
      return newItems.reduce((sum, item) => sum + (item.probability || 0), 0);
  };

  const totalProb = calculateTotalProbability();

  return (
      <div className="space-y-6 animate-in fade-in duration-300">
           <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-slate-700 pb-4 gap-4">
               <div><h2 className="text-xl font-bold text-white flex items-center gap-2"><Gift className="text-pink-400"/> Reward System</h2></div>
               <div className="flex bg-slate-800 p-1 rounded-lg">
                  <button onClick={() => setActiveTab('PHYSICAL')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${activeTab === 'PHYSICAL' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}><Box size={16} /> Physical / Loot</button>
                  <button onClick={() => setActiveTab('DIGITAL')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${activeTab === 'DIGITAL' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}><Coins size={16} /> Digital / Tokens</button>
               </div>
           </div>

           {/* Community Mystery Box — sponsor a prize that ANY user can win with points */}
           <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/20 border border-indigo-500/30 rounded-xl p-5">
             <div className="flex items-start gap-3 mb-4">
               <div className="h-10 w-10 rounded-xl bg-indigo-900/40 border border-indigo-500/30 flex items-center justify-center shrink-0">
                 <Sparkles className="text-indigo-300" size={20} />
               </div>
               <div>
                 <h3 className="text-white font-bold">Sponsor the Community Mystery Box</h3>
                 <p className="text-xs text-slate-400 mt-0.5">
                   Fund a prize and any user can win it by spending their points — regardless of survey.
                   You pay Tinaney the reward amount via KHQR/card; the prize drops into the box once paid.
                 </p>
               </div>
             </div>

             <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
               <div>
                 <label className="block text-[10px] uppercase text-slate-500 font-bold mb-1">Prize name</label>
                 <input value={sponsorName} onChange={e => setSponsorName(e.target.value)} placeholder="e.g. $5 phone top-up"
                   className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-indigo-500" />
               </div>
               <div>
                 <label className="block text-[10px] uppercase text-slate-500 font-bold mb-1">Amount</label>
                 <input type="number" min={1} step="1" value={sponsorAmount}
                   onChange={e => setSponsorAmount(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                   className="w-24 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-indigo-500" />
               </div>
               <div>
                 <label className="block text-[10px] uppercase text-slate-500 font-bold mb-1">Currency</label>
                 <select value={sponsorCurrency} onChange={e => setSponsorCurrency(e.target.value)}
                   className="bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-indigo-500">
                   <option value="USD">USD</option>
                   <option value="KHR">KHR</option>
                   <option value="USDT">USDT</option>
                 </select>
               </div>
               <button onClick={handleSponsor} disabled={sponsorBusy || !sponsorName.trim() || sponsorAmount <= 0}
                 className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white font-bold px-5 py-2 rounded-lg text-sm transition-colors">
                 {sponsorBusy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Sponsor
               </button>
             </div>

             {sponsorMsg && <p className="text-[11px] text-indigo-200 mt-3 bg-indigo-900/30 border border-indigo-500/30 rounded-lg px-3 py-2">{sponsorMsg}</p>}

             {sponsorships.length > 0 && (
               <div className="mt-4 pt-4 border-t border-slate-700/60 space-y-2">
                 <p className="text-[10px] uppercase text-slate-500 font-bold">Your sponsorships</p>
                 {sponsorships.map(s => (
                   <div key={s.id} className="flex items-center justify-between text-sm bg-slate-900/40 border border-slate-700 rounded-lg px-3 py-2">
                     <span className="text-slate-200 truncate pr-2">{s.reward_name} <span className="text-slate-500">· {s.amount} {s.currency}</span></span>
                     <div className="flex items-center gap-2 shrink-0">
                       {s.status === 'PENDING_PAYMENT' && <span className="text-[11px] text-yellow-300 flex items-center gap-1"><Clock size={12} /> Awaiting payment</span>}
                       {s.status === 'PAID' && <span className="text-[11px] text-emerald-300 flex items-center gap-1"><CheckCircle2 size={12} /> In the box</span>}
                       {s.status === 'CANCELLED' && <span className="text-[11px] text-slate-500">Cancelled</span>}
                       {s.status === 'PENDING_PAYMENT' && (
                         <button onClick={() => handleCancelSponsorship(s.id)} className="text-slate-500 hover:text-red-400"><X size={14} /></button>
                       )}
                     </div>
                   </div>
                 ))}
               </div>
             )}
           </div>

           {activeTab === 'PHYSICAL' && (
             <div className="animate-in fade-in slide-in-from-left-4">
               <div className="flex justify-between items-center mb-6">
                   <h3 className="text-white font-semibold flex items-center gap-2"><LayoutTemplate size={18} className="text-indigo-400" /> Loot Tables</h3>
                   <button 
                        onClick={() => { resetPhysicalForm(); setIsCreatingPhysical(true); if (newItems.length === 0) addItem(); }} 
                        className={`bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm ${isCreatingPhysical ? 'opacity-50 pointer-events-none' : ''}`}
                   >
                       <Plus size={16} /> Create Loot Table
                   </button>
               </div>
               {isCreatingPhysical && (
                   <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-xl mb-8 animate-in slide-in-from-top-4">
                       <div className="flex justify-between items-start mb-6 border-b border-slate-700 pb-4">
                           <div>
                               <h3 className="text-xl font-bold text-white">{editingLootTableId ? 'Edit Loot Table' : 'New Physical Reward Table'}</h3>
                               <p className="text-slate-400 text-sm mt-1">Configure a loot box with probability-weighted items.</p>
                           </div>
                           <div className={`px-3 py-1 rounded text-xs font-bold border flex items-center gap-1 ${Math.abs(totalProb - 100) < 0.1 ? 'bg-emerald-900/30 text-emerald-400 border-emerald-500/30' : 'bg-orange-900/30 text-orange-400 border-orange-500/30'}`}>
                               <Percent size={12} /> Total Prob: {totalProb.toFixed(1)}%
                           </div>
                       </div>

                       <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div><label className="block text-xs uppercase text-slate-500 font-semibold mb-2">Table Name *</label><input value={physicalName} onChange={e => setPhysicalName(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white text-sm focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="e.g. Rare Merch Drop 2024" /></div>
                            <div><label className="block text-xs uppercase text-slate-500 font-semibold mb-2">Description</label><input value={physicalDesc} onChange={e => setPhysicalDesc(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white text-sm focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="e.g. Limited edition items for survey participants" /></div>
                       </div>

                       {/* Mystery box: openable by users spending points */}
                       <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-8 flex items-center justify-between gap-4">
                           <label className="flex items-center gap-3 cursor-pointer select-none">
                               <input type="checkbox" checked={isMystery} onChange={e => setIsMystery(e.target.checked)} className="accent-indigo-500 w-4 h-4" />
                               <span className="text-sm text-slate-200 font-medium">Mystery Box <span className="text-slate-500 font-normal">— users open it by spending points</span></span>
                           </label>
                           {isMystery && (
                               <div className="flex items-center gap-2">
                                   <span className="text-xs uppercase text-slate-500 font-semibold">Cost</span>
                                   <input type="number" min={1} value={pointCost} onChange={e => setPointCost(parseInt(e.target.value) || 0)} className="w-24 bg-slate-800 border border-slate-600 rounded p-2 text-white text-sm text-center outline-none focus:border-indigo-500" />
                                   <span className="text-xs text-slate-400">pts</span>
                               </div>
                           )}
                       </div>

                       <div className="space-y-4 mb-8">
                           <div className="flex justify-between items-center px-1">
                               <label className="text-xs uppercase text-slate-500 font-semibold">Inventory Items</label>
                               <button onClick={addItem} className="text-indigo-400 text-sm flex items-center gap-1 font-bold hover:text-indigo-300 transition-colors"><Plus size={16} /> Add Item</button>
                           </div>
                           
                           {newItems.length === 0 && (
                               <div className="border-2 border-dashed border-slate-700 rounded-xl p-8 text-center text-slate-500">
                                   <Box size={32} className="mx-auto mb-2 opacity-50"/>
                                   No items added. Click "Add Item" to start.
                               </div>
                           )}

                           {newItems.map((item, idx) => (
                                <div key={idx} className="bg-slate-900/50 p-4 rounded-xl border border-slate-700 hover:border-slate-500 transition-colors relative group animate-in fade-in slide-in-from-bottom-2">
                                    <button onClick={() => removeItem(idx)} className="absolute top-3 right-3 text-slate-600 hover:text-red-400 p-2 hover:bg-slate-800 rounded-lg transition-colors z-10"><Trash2 size={16} /></button>
                                    
                                    <div className="flex flex-col md:flex-row gap-4 md:items-start">
                                        {/* Image Box */}
                                        <div className="w-16 h-16 md:w-20 md:h-20 bg-slate-800 rounded-lg border border-slate-600 flex items-center justify-center shrink-0 overflow-hidden relative group/img">
                                            {item.image_url ? (
                                                <img src={item.image_url} alt="Preview" className="w-full h-full object-cover" />
                                            ) : (
                                                <Gift className="text-slate-600" size={24} />
                                            )}
                                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 flex items-center justify-center transition-opacity pointer-events-none">
                                                <ImageIcon size={16} className="text-white"/>
                                            </div>
                                        </div>

                                        <div className="flex-1 space-y-4">
                                            {/* Top Row */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-[10px] text-slate-500 font-bold uppercase mb-1">Item Name</label>
                                                    <input value={item.name} onChange={e => updateItem(idx, 'name', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white focus:border-indigo-500 outline-none" placeholder="Item Name" />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] text-slate-500 font-bold uppercase mb-1">Image URL</label>
                                                    <input value={item.image_url || ''} onChange={e => updateItem(idx, 'image_url', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-xs text-slate-300 font-mono focus:border-indigo-500 outline-none truncate" placeholder="https://..." />
                                                </div>
                                            </div>

                                            {/* Bottom Row */}
                                            <div className="grid grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-[10px] text-slate-500 font-bold uppercase mb-1">Type</label>
                                                    <select value={item.type} onChange={e => updateItem(idx, 'type', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-2 text-xs text-white outline-none focus:border-indigo-500">
                                                        <option value="PHYSICAL">Item</option>
                                                        <option value="VOUCHER">Code</option>
                                                        <option value="NFT">NFT</option>
                                                        <option value="TOKEN">Token</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] text-slate-500 font-bold uppercase mb-1 flex items-center gap-1"><Package size={10}/> Stock</label>
                                                    <input type="number" value={item.total_qty} onChange={e => updateItem(idx, 'total_qty', parseFloat(e.target.value))} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-2 text-sm text-white text-center focus:border-indigo-500 outline-none" placeholder="Qty" />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] text-slate-500 font-bold uppercase mb-1 flex items-center gap-1"><Percent size={10}/> Chance</label>
                                                    <input type="number" value={item.probability} onChange={e => updateItem(idx, 'probability', parseFloat(e.target.value))} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-2 text-sm text-white text-center focus:border-indigo-500 outline-none" placeholder="%" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                           ))}
                       </div>
                       
                       <div className="flex justify-between items-center pt-6 border-t border-slate-700">
                           <div className="text-xs text-slate-500">
                               {Math.abs(totalProb - 100) > 0.1 && <span className="text-orange-400 flex items-center gap-1"><AlertCircle size={12}/> Probabilities should sum to 100%</span>}
                           </div>
                           <div className="flex gap-3">
                               <button onClick={resetPhysicalForm} className="text-slate-400 hover:text-white px-4 py-2 text-sm font-medium">Cancel</button>
                               <button onClick={handleCreateOrUpdatePhysical} disabled={!physicalName || newItems.length === 0} className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2.5 rounded-lg font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95">
                                   {editingLootTableId ? 'Update Table' : 'Save Table'}
                               </button>
                           </div>
                       </div>
                   </div>
               )}
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   {lootTables.map(t => (
                       <div key={t.id} className="bg-slate-800 p-4 rounded-xl border border-slate-700 hover:border-indigo-500/50 transition-colors group relative">
                           <div className="flex justify-between items-start mb-3">
                               <div>
                                   <h4 className="font-bold text-white">{t.name}</h4>
                                   {t.description && <p className="text-xs text-slate-500">{t.description}</p>}
                               </div>
                               <div className="flex gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity absolute top-4 right-4">
                                   <button 
                                        onClick={() => handleEditPhysical(t)} 
                                        className="text-slate-400 hover:text-indigo-400 p-1.5 rounded hover:bg-slate-700 transition-colors"
                                        title="Edit Table"
                                   >
                                       <Edit2 size={16}/>
                                   </button>
                                   <button 
                                        onClick={() => handleDeletePhysical(t.id)} 
                                        className="text-slate-400 hover:text-red-400 p-1.5 rounded hover:bg-slate-700 transition-colors"
                                        title="Delete Table"
                                   >
                                       <Trash2 size={16}/>
                                   </button>
                               </div>
                           </div>
                           <div className="space-y-2 mt-3 bg-slate-900/50 p-3 rounded-lg">
                               {t.items && t.items.map((item, i) => (
                                   <div key={i} className="flex justify-between text-sm items-center border-b border-slate-800 last:border-0 pb-2 last:pb-0 mb-2 last:mb-0">
                                       <div className="flex items-center gap-2">
                                           {item.image_url ? (<img src={item.image_url} className="w-6 h-6 rounded object-cover border border-slate-700" alt="" />) : (<div className="w-6 h-6 rounded bg-slate-700 flex items-center justify-center"><Box size={12}/></div>)}
                                           <span className="text-slate-300">{item.name}</span>
                                       </div>
                                       <div className="flex items-center gap-3">
                                           <span className="text-xs text-slate-500 font-mono">Qty: {item.remaining_qty}/{item.total_qty}</span>
                                           <span className="text-indigo-400 font-mono text-xs font-bold">{item.probability}%</span>
                                       </div>
                                   </div>
                               ))}
                           </div>
                       </div>
                   ))}
                   {lootTables.length === 0 && <p className="text-slate-500 italic col-span-2 text-center py-8">No physical reward tables configured.</p>}
               </div>
             </div>
           )}
           
           {activeTab === 'DIGITAL' && (
             <div className="animate-in fade-in slide-in-from-right-4">
                <div className="flex justify-between items-center mb-6"><h3 className="text-white font-semibold flex items-center gap-2"><Coins size={18} className="text-emerald-400" /> Digital Rewards</h3><button onClick={() => setIsCreatingDigital(true)} className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm"><Plus size={16} /> New Digital Reward</button></div>
                {isCreatingDigital && (
                   <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-xl mb-8 animate-in slide-in-from-top-4">
                       <h3 className="text-lg font-bold text-white mb-4">Configure Digital Reward</h3>
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                           <div className="col-span-2"><label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Configuration Name</label><input value={digiName} onChange={e => setDigiName(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm" placeholder="e.g. Standard Survey Reward" /></div>
                           <div><label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Amount</label><input type="number" step="0.01" value={digiAmount} onChange={e => setDigiAmount(parseFloat(e.target.value))} className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm" /></div>
                           <div><label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Currency Symbol</label><input value={digiCurrency} onChange={e => setDigiCurrency(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm" placeholder="USDT" /></div>
                           <div><label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Blockchain</label><select value={digiChain} onChange={e => setDigiChain(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm"><option value="BSC">Binance Smart Chain (BSC)</option><option value="POLYGON">Polygon (MATIC)</option><option value="ETHEREUM">Ethereum (ETH)</option><option value="CAM_CHAIN">CamChain (Private)</option></select></div>
                           <div><label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Contract Address (Optional)</label><input value={digiContract} onChange={e => setDigiContract(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm font-mono" placeholder="0x..." /></div>
                       </div>
                       <div className="flex justify-end gap-3"><button onClick={() => setIsCreatingDigital(false)} className="text-slate-400 hover:text-white px-4">Cancel</button><button onClick={handleCreateDigital} className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded-lg font-bold">Save Configuration</button></div>
                   </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{digitalRewards.map(r => (<div key={r.id} className="bg-slate-800 p-5 rounded-xl border border-slate-700 hover:border-indigo-500/50 transition-colors group relative"><div className="absolute top-4 right-4"><button onClick={() => handleDeleteDigital(r.id)} className="text-slate-600 hover:text-red-400 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"><Trash2 size={16}/></button></div><h4 className="font-bold text-white mb-1">{r.name}</h4><div className="flex items-baseline gap-1 text-emerald-400 font-mono text-2xl font-bold mt-2"><Coins size={20} /> {r.amount} {r.currency}</div><div className="mt-3 pt-3 border-t border-slate-700 flex justify-between text-xs text-slate-500"><span>{r.chain}</span>{r.contract_address ? (<span className="font-mono flex items-center gap-1"><Link size={10}/> {r.contract_address.substring(0,6)}...</span>) : (<span>Native</span>)}</div></div>))}{digitalRewards.length === 0 && <p className="text-slate-500 italic col-span-3 text-center py-8">No digital rewards configured.</p>}</div>
             </div>
           )}
           
           <div className="space-y-4 pt-8 border-t border-slate-700 mt-8"><h3 className="text-white font-semibold flex items-center gap-2"><Database size={18} className="text-slate-400" /> Recent Activity Logs</h3><div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden"><table className="w-full text-left text-sm"><thead className="bg-slate-900 text-slate-400 text-xs uppercase"><tr><th className="px-4 py-2">User</th><th className="px-4 py-2">Action</th><th className="px-4 py-2 text-right">Amount</th></tr></thead><tbody className="divide-y divide-slate-700">{logs.map(log => (<tr key={log.id}><td className="px-4 py-2 font-mono text-xs text-slate-500">{log.user_id ? log.user_id.substring(0,8) + '...' : 'Anonymous (Kiosk)'}</td><td className="px-4 py-2 text-slate-300">{log.action_type}</td><td className="px-4 py-2 text-right text-emerald-400 font-bold">{log.amount ? `+${log.amount} ${log.currency}` : log.item_name}</td></tr>))}{logs.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-500 italic">No rewards distributed yet.</td></tr>}</tbody></table></div></div>
      </div>
  );
};

export default ResearchRewardView;
